'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

export type SubmenuKey = 'brand_visibility' | 'domain_impact'

function routeSubmenu(pathname: string): SubmenuKey | null {
  if (pathname.startsWith('/citations')) return 'brand_visibility'
  if (pathname.startsWith('/publisher')) return 'domain_impact'
  return null
}

/**
 * Insights submenu state — both submenus can be expanded independently.
 * Default: both collapsed. Navigating into a submenu's route auto-expands
 * that submenu (without collapsing the other). Manual toggle of an individual
 * submenu sticks until the user toggles it again.
 */
export default function useSidebarSubmenus(): {
  isExpanded: (key: SubmenuKey) => boolean
  toggle: (key: SubmenuKey) => void
  /** Submenu that owns the current route, or null if outside Insights. */
  activeSubmenu: SubmenuKey | null
} {
  const pathname = usePathname() ?? '/'
  const active = routeSubmenu(pathname)

  const [expanded, setExpanded] = useState<Set<SubmenuKey>>(
    () => (active ? new Set([active]) : new Set()),
  )

  const lastPathRef = useRef(pathname)
  useEffect(() => {
    if (lastPathRef.current === pathname) return
    lastPathRef.current = pathname
    if (active) {
      setExpanded((prev) => {
        if (prev.has(active)) return prev
        const next = new Set(prev)
        next.add(active)
        return next
      })
    }
  }, [pathname, active])

  const isExpanded = useCallback(
    (key: SubmenuKey) => expanded.has(key),
    [expanded],
  )

  const toggle = useCallback((key: SubmenuKey) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  return { isExpanded, toggle, activeSubmenu: active }
}
